import React, { memo, useCallback, useState, useMemo } from 'react'
import { get } from 'lodash'
import { FastField, FieldArray, FormikProps, ArrayHelpers } from 'formik'
import {
  TextField as FormikTextField,
  Select as FormikSelect,
  JsonEditor as FormikJsonEditor,
  Switch as FormikSwitch,
} from 'src/ui/formik'
import { FormControl, Input } from 'src/ui/form-controls'
import {
  Box,
  Row,
  Column,
  Panel,
  IconButton,
  Tooltip,
  DeleteIcon,
  AddIcon,
  Switch,
  Link,
} from 'src/ui'
import { Radio, RadioGroup, Stack, Text, Alert, AlertIcon } from '@chakra-ui/react'
import {
  cronExplained,
  newScheduleGroup,
  newIndividualSchedule,
  randKey,
  checkDuplicateSchedule,
} from 'src/translators/form/utils'

interface IndividualScheduleProps {
  readonly groupIndex: number
  readonly scheduleIndex: number
  readonly schedule: any
  readonly formikProps: FormikProps<any>
  readonly deleteSchedule: (index: number) => void
  readonly translatorId?: string
  readonly companySlug?: string
  readonly adapterModule?: string
  readonly scheduleGroupAdapter?: string
}

const IndividualSchedule = memo(function IndividualSchedule({
  groupIndex,
  scheduleIndex,
  formikProps,
  deleteSchedule,
  translatorId,
  companySlug,
  adapterModule,
  scheduleGroupAdapter,
}: IndividualScheduleProps) {
  const scopedFieldName = useCallback(
    (fieldName: string) => `schedules[${groupIndex}].schedules[${scheduleIndex}].${fieldName}`,
    [groupIndex, scheduleIndex]
  )

  const [userPreferredTimingMode, setUserPreferredTimingMode] = useState('interval')
  const [userPreferredStartMode, setUserPreferredStartMode] = useState(() => {
    const sd = get(formikProps.values, scopedFieldName('start_date'))
    const sda = get(formikProps.values, scopedFieldName('start_days_ago'))
    if (sd) return 'date'
    if (sda) return 'days_ago'
    return 'all_time'
  })
  const [isEditing, setIsEditing] = useState(false)

  // Get all schedules in the current group
  const allSchedulesInGroup = get(formikProps.values, `schedules[${groupIndex}].schedules`, [])
  const currentSchedule = allSchedulesInGroup[scheduleIndex]

  // Check if this schedule is a duplicate
  const isDuplicate = useMemo(() => {
    return checkDuplicateSchedule(currentSchedule, scheduleIndex, allSchedulesInGroup)
  }, [currentSchedule, scheduleIndex, allSchedulesInGroup])

  // Determine which timing mode is active
  const intervalMinutes = get(formikProps.values, scopedFieldName('interval_minutes'))
  const cron = get(formikProps.values, scopedFieldName('cron'))
  let timingMode = userPreferredTimingMode
  if (intervalMinutes) {
    timingMode = 'interval'
  } else if (cron) {
    timingMode = 'cron'
  }

  // Determine which start mode is active
  const startDate = get(formikProps.values, scopedFieldName('start_date'))
  const startDaysAgo = get(formikProps.values, scopedFieldName('start_days_ago'))
  let startMode: string
  if (startDate) {
    startMode = 'date'
  } else if (startDaysAgo) {
    startMode = 'days_ago'
  } else if (isEditing && userPreferredStartMode) {
    startMode = userPreferredStartMode
  } else {
    startMode = 'all_time'
  }

  const handleTimingModeChange = (value: string) => {
    setUserPreferredTimingMode(value)
    if (value === 'interval') {
      formikProps.setFieldValue(scopedFieldName('cron'), '')
    } else {
      formikProps.setFieldValue(scopedFieldName('interval_minutes'), '')
    }
  }

  const handleStartModeChange = (value: string) => {
    setUserPreferredStartMode(value)
    if (value === 'all_time') {
      setIsEditing(false)
    } else {
      setIsEditing(true)
    }

    if (value === 'days_ago') {
      formikProps.setFieldValue(scopedFieldName('start_date'), '')
    } else if (value === 'date') {
      formikProps.setFieldValue(scopedFieldName('start_days_ago'), '')
    } else if (value === 'all_time') {
      // Clear both for 'All Time'
      formikProps.setFieldValue(scopedFieldName('start_date'), '')
      formikProps.setFieldValue(scopedFieldName('start_days_ago'), '')
    }
  }

  return (
    <Box
      borderLeft={isDuplicate ? '3px solid #e53e3e' : '3px solid #cbd5e0'}
      pl={3}
      ml={2}
      mb={1}
      py={3}
      bg={isDuplicate ? '#fff5f5' : '#f7fafc'}
      borderRadius={3}
    >
      <Row justifyContent="space-between" alignItems="center" mb={1}>
        <Row alignItems="center" gap={2}>
          <Box fontWeight="semibold" fontSize="sm" color="gray.700">
            Schedule {scheduleIndex + 1}
          </Box>
          {translatorId && companySlug && adapterModule && currentSchedule?.id && (
            <Tooltip label="View Datadog Logs" placement="top">
              <Link
                href={`https://app.datadoghq.com/logs?query=service%3Abushel-integrations-adapter-${adapterModule}%20container_name%3Aadapter-${translatorId}-worker%20%40context.company.slug%3A${companySlug}${scheduleGroupAdapter ? `%20%40context.schedule.adapter%3A${scheduleGroupAdapter}` : ''}%20%40context.job_name%3A${currentSchedule.name || currentSchedule.id}&cols=service%2Cmessage%2C%40context.company.slug%2C%40context.schedule.adapter%2C%40context.job_name%2C%40context.metrics.page_duration%2C%40context.metrics.page_size&messageDisplay=inline&stream_sort=time%2Cdesc&viz=stream&live=true`}
                isExternal
                display="inline-flex"
                alignItems="center"
              >
                <Box
                  as="img"
                  src="/datadog-logo.png"
                  alt="Datadog"
                  width="22px"
                  height="22px"
                  filter="invert(27%) sepia(98%) saturate(2476%) hue-rotate(256deg) brightness(75%) contrast(101%)"
                />
              </Link>
            </Tooltip>
          )}
        </Row>
        <Tooltip label="Remove Schedule" aria-label="Remove Schedule" placement="left">
          <IconButton
            icon={<DeleteIcon />}
            aria-label="Delete"
            size="sm"
            isRound
            variant="ghost"
            color="gray.600"
            onClick={() => deleteSchedule(scheduleIndex)}
          />
        </Tooltip>
      </Row>

      {isDuplicate && (
        <Alert status="warning" mb={2} borderRadius="md" fontSize="sm">
          <AlertIcon />
          Duplicate schedule detected: Another schedule in this group has the same scrubber setting
          and start time.
        </Alert>
      )}

      <Row mb={2} alignItems="flex-start" gap={6}>
        {' '}
        {/* Priority and Scrubber */}
        <Box flex={1}>
          <FastField
            name={scopedFieldName('priority')}
            label="Priority"
            component={FormikTextField}
          />
        </Box>
        <Box flex={1} pt={2}>
          <FastField
            name={scopedFieldName('is_scrubber')}
            label="Scrubber"
            component={FormikSwitch}
          />
        </Box>
      </Row>

      <Row alignItems="flex-start" gap={6} mb={2}>
        {' '}
        {/* Combined Timing and Start Time */}
        {/* Timing: Interval vs CRON */}
        <Box flex="1">
          <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.500">
            Schedule Timing
          </Text>
          {(() => {
            const intervalError = get(formikProps.errors, scopedFieldName('interval_minutes'))
            const cronError = get(formikProps.errors, scopedFieldName('cron'))
            const intervalTouched = get(formikProps.touched, scopedFieldName('interval_minutes'))
            const cronTouched = get(formikProps.touched, scopedFieldName('cron'))
            const shouldShowError =
              (formikProps.submitCount > 0 || intervalTouched || cronTouched) &&
              (intervalError || cronError)

            return shouldShowError ? (
              <Alert status="error" mb={2} borderRadius="md" fontSize="xs" py={1}>
                <>
                  <AlertIcon boxSize="12px" />
                  {intervalError || cronError}
                </>
              </Alert>
            ) : null
          })()}
          <Row alignItems="center" gap={4}>
            <RadioGroup value={timingMode} onChange={handleTimingModeChange} size="sm">
              <Stack direction="row" spacing={4} h="40px" alignItems="center">
                <Radio value="interval">Interval</Radio>
                <Radio value="cron">CRON</Radio>
              </Stack>
            </RadioGroup>
            <Box flex="1" pr={3}>
              {timingMode === 'interval' ? (
                <FastField
                  name={scopedFieldName('interval_minutes')}
                  placeholder="Minutes (e.g. 120)"
                  component={FormikTextField}
                  InputProps={{ h: '40px', lineHeight: '40px' }}
                  formControlProps={{ mb: 0, h: 'auto' }}
                />
              ) : (
                <FastField name={scopedFieldName('cron')}>
                  {({ field, form }) => {
                    const isTouched = get(form.touched, field.name)
                    const errorMessage = get(form.errors, field.name)
                    const hasError =
                      !!errorMessage && (isTouched || form.submitCount > 0 || form.isValidating)

                    return (
                      <FormControl isInvalid={hasError} mb={0} height="auto">
                        <Input
                          {...field}
                          id={field.name}
                          placeholder="e.g., 0 */2 * * *"
                          height="40px"
                          lineHeight="40px"
                        />
                        <Text fontSize="xs" color={hasError ? 'red.500' : 'gray.500'} mt={1} pl={1}>
                          {hasError && errorMessage ? errorMessage : cronExplained(field.value)}
                        </Text>
                      </FormControl>
                    )
                  }}
                </FastField>
              )}
            </Box>
          </Row>
        </Box>
        {/* Start: Days Ago vs Specific Date */}
        <Box flex="1">
          <Row justifyContent="space-between" alignItems="center" mb={2} pr={3}>
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              Start Time
            </Text>
            <Row alignItems="center" gap={2}>
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                All Time
              </Text>
              <Switch
                size="sm"
                isChecked={startMode === 'all_time'}
                onChange={(e) => handleStartModeChange(e.target.checked ? 'all_time' : 'days_ago')}
              />
            </Row>
          </Row>
          <Row alignItems="center" gap={4}>
            <RadioGroup
              value={startMode === 'all_time' ? undefined : startMode}
              onChange={handleStartModeChange}
              size="sm"
              isDisabled={startMode === 'all_time'}
            >
              <Stack direction="row" spacing={4} h="40px" alignItems="center">
                <Radio value="days_ago">Days Ago</Radio>
                <Radio value="date">Date</Radio>
              </Stack>
            </RadioGroup>
            <Box flex="1" pr={3}>
              {startMode === 'days_ago' && (
                <FastField name={scopedFieldName('start_days_ago')}>
                  {({ field, form }) => (
                    <FormikTextField
                      field={field}
                      form={form}
                      placeholder="Days (e.g. 10)"
                      InputProps={{
                        h: '40px',
                        lineHeight: '40px',
                        onBlur: (e: any) => {
                          field.onBlur(e)
                          setIsEditing(false)
                        },
                        onFocus: () => setIsEditing(true),
                      }}
                      formControlProps={{ mb: 0, h: 'auto' }}
                    />
                  )}
                </FastField>
              )}
              {startMode === 'date' && (
                <FastField name={scopedFieldName('start_date')}>
                  {({ field, form }) => (
                    <FormikTextField
                      field={field}
                      form={form}
                      placeholder="YYYY-MM-DD"
                      InputProps={{
                        h: '40px',
                        lineHeight: '40px',
                        onBlur: (e: any) => {
                          field.onBlur(e)
                          setIsEditing(false)
                        },
                        onFocus: () => setIsEditing(true),
                      }}
                      formControlProps={{ mb: 0, h: 'auto' }}
                    />
                  )}
                </FastField>
              )}
              {startMode === 'all_time' && (
                <FormControl mb={0} height="auto">
                  <Input
                    value="Start from beginning"
                    isDisabled
                    height="40px"
                    lineHeight="40px"
                    bg="gray.100"
                    color="gray.500"
                  />
                </FormControl>
              )}
            </Box>
          </Row>
        </Box>
      </Row>
    </Box>
  )
})

const hasErrors = ({ formikProps, index }: { formikProps: FormikProps<any>; index: number }) => {
  if (formikProps.submitCount === 0) return false
  let errs = get(formikProps.errors, `schedules[${index}]`)
  if (!errs) return false
  return Object.keys(errs).length > 0
}

interface ScheduleGroupProps {
  readonly index: number
  readonly expanded: boolean
  readonly scheduleGroup: any
  readonly formikProps: FormikProps<any>
  readonly toggleExpanded: (key: string, expanded: boolean) => void
  readonly deleteScheduleGroup: (index: number) => void
  readonly translatorId?: string
  readonly companySlug?: string
  readonly adapterModule?: string
}

const ScheduleGroup = memo(function ScheduleGroup({
  index,
  expanded,
  scheduleGroup,
  toggleExpanded,
  deleteScheduleGroup,
  formikProps,
  translatorId,
  companySlug,
  adapterModule,
}: ScheduleGroupProps) {
  const scopedFieldName = useCallback(
    (fieldName: string) => `schedules[${index}].${fieldName}`,
    [index]
  )
  const { key, adapter, schedules } = scheduleGroup

  return (
    <Panel
      expanded={expanded}
      onChange={(_e, isExpanded) => toggleExpanded(key, isExpanded)}
      header={
        <Row width="100%" justifyContent="space-between" alignItems="center">
          <Row alignItems="center" gap={2}>
            <Box>
              {adapter ? <strong>{adapter}</strong> : <em>new_schedule_group</em>}
              {schedules && schedules.length > 0 && (
                <Box as="span" ml={2} fontSize="sm" color="gray.600">
                  ({schedules.length} schedule{schedules.length === 1 ? '' : 's'})
                </Box>
              )}
            </Box>
            {translatorId && companySlug && adapterModule && (
              <Tooltip label="View Datadog Logs (All Schedules)" placement="top">
                <Link
                  href={`https://app.datadoghq.com/logs?query=service%3Abushel-integrations-adapter-${adapterModule}%20container_name%3Aadapter-${translatorId}-worker%20%40context.company.slug%3A${companySlug}${adapter ? `%20%40context.schedule.adapter%3A${adapter}` : ''}&cols=service%2Cmessage%2C%40context.company.slug%2C%40context.schedule.adapter%2C%40context.job_name%2C%40context.metrics.page_duration%2C%40context.metrics.page_size&messageDisplay=inline&stream_sort=time%2Cdesc&viz=stream&live=true`}
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Box
                    as="img"
                    src="/datadog-logo.png"
                    alt="Datadog"
                    width="22px"
                    height="22px"
                    filter="invert(27%) sepia(98%) saturate(2476%) hue-rotate(256deg) brightness(75%) contrast(101%)"
                  />
                </Link>
              </Tooltip>
            )}
          </Row>

          <Box>
            <Tooltip
              label="Remove Schedule Group"
              aria-label="Remove Schedule Group"
              placement="left"
            >
              <IconButton
                icon={<DeleteIcon />}
                aria-label="Delete"
                isRound
                variant="ghost"
                color="gray.600"
                onClick={(e) => {
                  e.stopPropagation()
                  deleteScheduleGroup(index)
                }}
              />
            </Tooltip>
          </Box>
        </Row>
      }
    >
      <Column width="100%" pr={1} pl={1}>
        <Box bg="#f7fafc" borderLeft="3px solid #cbd5e0" p={3} pb={10} borderRadius={3} mb={2}>
          <Box fontWeight="bold" mb={2} fontSize="sm" color="gray.700">
            Core Settings
          </Box>

          <Row mb={3} gap={6}>
            <Box flex={1}>
              <FastField
                name={scopedFieldName('adapter')}
                label="Adapter Name"
                component={FormikTextField}
              />
            </Box>
            <Box flex={1}>
              <FastField
                id={scopedFieldName('type')}
                name={scopedFieldName('type')}
                label="Fetch Type"
                component={FormikSelect}
                selectProps={{ isClearable: false }}
                options={[
                  { value: 'Bulk', label: 'Bulk' },
                  { value: 'ById', label: 'ById' },
                  { value: 'ByUser', label: 'ByUser' },
                ]}
              />
            </Box>
          </Row>

          <Box>
            <FastField
              name={scopedFieldName('adapter_config')}
              label="Adapter Config (JSON)"
              component={FormikJsonEditor}
              height={180}
            />
          </Box>
        </Box>

        <Box>
          <Row
            justifyContent="space-between"
            alignItems="center"
            mb={1}
            pb={1}
            borderBottom="1px solid #e2e8f0"
          >
            <Box fontWeight="bold" fontSize="sm" color="gray.700">
              Schedules
            </Box>
            <Tooltip label="Add Schedule" aria-label="Add Schedule" placement="top">
              <IconButton
                icon={<AddIcon />}
                aria-label="Add Schedule"
                size="sm"
                isRound
                variant="ghost"
                color="gray.600"
                onClick={() => {
                  const currentSchedules =
                    get(formikProps.values, scopedFieldName('schedules')) || []
                  const newSchedule = { ...newIndividualSchedule, key: randKey() }
                  formikProps.setFieldValue(scopedFieldName('schedules'), [
                    ...currentSchedules,
                    newSchedule,
                  ])
                }}
              />
            </Tooltip>
          </Row>

          <FieldArray
            name={scopedFieldName('schedules')}
            render={(arrayHelpers: ArrayHelpers) => (
              <>
                {schedules && schedules.length > 0 ? (
                  schedules.map((schedule, scheduleIndex) => (
                    <IndividualSchedule
                      key={schedule.key || scheduleIndex}
                      groupIndex={index}
                      scheduleIndex={scheduleIndex}
                      schedule={schedule}
                      formikProps={formikProps}
                      deleteSchedule={arrayHelpers.remove}
                      translatorId={translatorId}
                      companySlug={companySlug}
                      adapterModule={adapterModule}
                      scheduleGroupAdapter={adapter}
                    />
                  ))
                ) : (
                  <Box px={3} py={2} fontSize="sm" color="gray.600" fontStyle="italic">
                    No individual schedules added yet. At least one is recommended.
                  </Box>
                )}
              </>
            )}
          />
        </Box>
      </Column>
    </Panel>
  )
})

interface ScheduleGroupListProps {
  readonly formikProps: FormikProps<any>
  readonly translatorId?: string
  readonly companySlug?: string
  readonly adapterModule?: string
}

function ScheduleGroupList({
  formikProps,
  translatorId,
  companySlug,
  adapterModule,
}: ScheduleGroupListProps) {
  const [expanded, setExpanded] = useState<string[]>([])

  const toggleExpanded = useCallback((key: string, isExpanded: boolean) => {
    if (isExpanded) {
      setExpanded((state) => [...state, key])
    } else {
      setExpanded((state) => state.filter((k) => k !== key))
    }
  }, [])

  // Check if this is a V2 config - only use explicit version field
  // V1 configs will not have a version field, V2+ will be explicitly versioned
  const isV2 = formikProps.values.version === '2'

  return (
    <FieldArray
      name="schedules"
      render={(arrayHelpers: ArrayHelpers) => (
        <Column width="100%" borderRadius={3} mb={5}>
          <Row
            justifyContent="space-between"
            alignItems="center"
            height="3.5rem"
            bg="#eee"
            borderRadius={3}
            fontWeight="bold"
            pr={2}
            pl={3}
            mb={3}
          >
            <Row alignItems="center">
              <Box>Schedule Groups</Box>
              {isV2 && (
                <Box ml={2} fontSize="xs" fontWeight="normal" color="gray.600">
                  (Version 2)
                </Box>
              )}
            </Row>

            <Row alignItems="center">
              <Box>
                <Tooltip label="Add Schedule Group" aria-label="Add Schedule Group" placement="top">
                  <IconButton
                    icon={<AddIcon />}
                    aria-label="Add Schedule Group"
                    size="sm"
                    isRound
                    variant="ghost"
                    color="gray.600"
                    onClick={() => {
                      const scheduleGroup = {
                        ...newScheduleGroup,
                        key: randKey(),
                        schedules: [],
                      }
                      arrayHelpers.unshift(scheduleGroup)
                      setExpanded((state) => {
                        return [...state, scheduleGroup.key]
                      })
                    }}
                  />
                </Tooltip>
              </Box>
            </Row>
          </Row>

          {formikProps.values.schedules && formikProps.values.schedules.length > 0 ? (
            formikProps.values.schedules.map((scheduleGroup, index) => (
              <ScheduleGroup
                key={scheduleGroup.key}
                index={index}
                scheduleGroup={scheduleGroup}
                formikProps={formikProps}
                expanded={expanded.includes(scheduleGroup.key) || hasErrors({ formikProps, index })}
                toggleExpanded={toggleExpanded}
                deleteScheduleGroup={arrayHelpers.remove}
                translatorId={translatorId}
                companySlug={companySlug}
                adapterModule={adapterModule}
              />
            ))
          ) : (
            <Row px={3}>
              <em>No schedule groups added yet</em>
            </Row>
          )}
        </Column>
      )}
    />
  )
}

export default ScheduleGroupList
